import './batchImport.scss';
import { components } from 'common';
import React, { useState, useRef } from 'react';
import { SnackBar, Modal } from '@umu/umu-design';
import UploadFailed from './components/uploadFailed/uploadFailed';
import UploadSuccess from './components/uploadSuccess/uploadSuccess';
import UploadProgress from './components/uploadProgress/uploadProgress';
import UploadIntroduction from './components/uploadIntroduction/uploadIntroduction';
import UploadIntroductionTableTitle from './components/uploadIntroductionTableTitle/uploadIntroductionTableTitle';
import { encode, formatFailedRecordsData, handleUploadProgressStatus, upload } from './dataflow';
import {
    IDataSource,
    IProgressRes,
    IUpLoadFileRes,
    SHOW_TYPE,
    UPLOAD_RESULT,
    BatchImport as IBatchImport,
    DownloadType,
    BatchImportType,
    UploadDataRequest,
    UploadData,
} from './interface';
import { generalRequest, binaryUpload, objectUpload, objectUploadProgress, downloadExcelFile } from './service';
import { IColumn } from 'common/src/components/customTable/interface';
import { lang } from '@umu/ukit-lang';

const { confirm } = Modal;
const { If, CustomTable } = components;
const UPLOAD_PROGRESS_POLLING_TIME = 300;

function BatchImport(props: IBatchImport) {
    const {
        userId,
        callback,
        className,
        id: propsId,
        customNotes,
        sizeMessage,
        uploadFailed,
        uploadDataMap,
        limitFileSize,
        customFailMsg,
        isHiddenNotes,
        downloadDataMap,
        tableColumnsMap,
        customSuccessMsg,
        isHiddenTableTitle,
        uploadIntroduction,
        tableTdWidth = 100,
        isMultilevel = false,
        limitShowFailedCount = 20,
    } = props;

    const instanceId = useRef('');
    const inputRef = useRef<HTMLInputElement>(null);
    const [isUpLoad, setIsUpLoad] = useState(false);
    const [uploadProgress, setProgress] = useState(0);
    const [totalNumber, setTotalNumber] = useState(0);
    const [failedNumber, setFailedNumber] = useState(0);
    const [showType, setShowType] = useState<SHOW_TYPE>(SHOW_TYPE.DESC_PANEL);
    const [failedCustomTableData, setFailedCustomTableData] = useState<IDataSource[]>([]);
    const [uploadFailedColumns, setUploadFailedColumns] = useState<any>(uploadFailed?.columns || []);

    const onUploadStatusChange = (flag: boolean) => {
        setProgress(0);
        setIsUpLoad(flag);
    };

    const handleSetProgress50 = () => {
        setProgress((oldProgress) => {
            if (oldProgress < 50) {
                return 50;
            } else {
                return oldProgress;
            }
        });
    };

    const setErrorRecordData = (data: any) => {
        let { list } = data;
        let columns = uploadFailedColumns;
        const { table_header, failed_records } = data;

        list = list || failed_records;

        if (table_header?.length) {
            columns = table_header.map((title: string, index: number) => {
                const obj: IColumn = {
                    title,
                    name: title,
                    dataIndex: String(index),
                };
                if (tableColumnsMap) {
                    const tableColumnsMapNew =
                        typeof tableColumnsMap === 'function' ? tableColumnsMap(table_header) : tableColumnsMap;
                    Object.assign(obj, tableColumnsMapNew[index]);
                }
                return obj;
            });
        }

        setUploadFailedColumns(columns);
        setFailedCustomTableData(formatFailedRecordsData(list, columns, isMultilevel));
    };

    const queryErrorRecordData = async () => {
        const { uploadErrorRecord } = uploadDataMap;
        if (!uploadErrorRecord) {
            return;
        }
        const id = propsId || instanceId.current;
        const { data } = await generalRequest(uploadErrorRecord, id);
        setErrorRecordData(data);
    };

    const handleErrorComplete = (res: any) => {
        const {
            data,
            data: { failed_records },
        } = res;
        if (failed_records) {
            setErrorRecordData(data);
        } else {
            queryErrorRecordData();
        }
    };

    const handleUploadProgressResponse = (res: any, progressFun: any, progressFunParams?: any) => {
        const {
            status,
            num,
            obj: { total, failed },
        } = handleUploadProgressStatus(res);

        if (status === UPLOAD_RESULT.SUCCESS_COMPLETE) {
            setIsUpLoad(false);
            setTotalNumber(total);
            setFailedNumber(failed);
            setShowType(SHOW_TYPE.SUCCESS_PANEL);
        } else if (status === UPLOAD_RESULT.IN_PROGRESS || status === UPLOAD_RESULT.NO_START) {
            setProgress((oldProgress) => {
                return Math.max(num, oldProgress);
            });
            setTimeout(() => {
                progressFun(progressFunParams);
            }, UPLOAD_PROGRESS_POLLING_TIME);
        } else if (status === UPLOAD_RESULT.ERROR_COMPLETE) {
            setIsUpLoad(false);
            setTotalNumber(total);
            setFailedNumber(failed);
            setShowType(SHOW_TYPE.FAIL_PANEL);
            handleErrorComplete(res);
        }
    };

    const handleCatchHandle = (error: any) => {
        setIsUpLoad(false);
        setShowType(SHOW_TYPE.DESC_PANEL);
        let text = error;
        if (typeof error === 'object') {
            text = error.error_message || error.message;
        }
        if (!text) {
            text = lang('上传失败');
        }
        confirm({
            title: lang('上传失败'),
            content: text,
            centered: true,
            okText: lang('确认'),
            wrapClassName: 'assessment-detail-upload-file-fail',
            onOk() {
                //
            },
        });
    };

    const upLoadCallBack = (instance_id?: string) => {
        handleSetProgress50();
        const { uploadProgress } = uploadDataMap;
        objectUploadProgress(uploadProgress as UploadData, instance_id)
            .then((data: IProgressRes) => {
                handleUploadProgressResponse(data, upLoadCallBack, instance_id);
            })
            .catch((error) => {
                handleCatchHandle(error);
            });
    };

    const handleBatchImportOfBinaryProgress = async () => {
        handleSetProgress50();
        const { uploadProgress } = uploadDataMap;
        generalRequest(uploadProgress as UploadData)
            .then((res) => {
                handleUploadProgressResponse(res, handleBatchImportOfBinaryProgress);
            })
            .catch((error) => {
                handleCatchHandle(error);
            });
    };

    const upLoadSuccess = (url: string) => {
        instanceId.current = '';
        setProgress(30);
        const { uploadExcel } = uploadDataMap;

        objectUpload(uploadExcel as UploadDataRequest, url, propsId)
            .then((res: { data: IUpLoadFileRes; error_message?: string }) => {
                if (!res || !res.data) {
                    handleCatchHandle(res.error_message);
                    return;
                }
                const { status, instance_id } = res.data;
                if (instance_id !== '' || Number(status) === 0) {
                    instance_id && (instanceId.current = instance_id);
                    upLoadCallBack(instance_id || propsId);
                }
            })
            .catch((error) => {
                handleCatchHandle(error);
            });
    };

    const downFile = (type: DownloadType) => {
        const id = propsId || instanceId.current;
        const downloadData = downloadDataMap[type];
        if (downloadData) {
            downloadExcelFile(downloadData, id);
        }
    };

    const handleBatchImportOfBinaryUpload = async (file: File) => {
        const { uploadExcel } = uploadDataMap;
        if (uploadExcel) {
            instanceId.current = '';
            setProgress(30);
            const res = await binaryUpload({ api: uploadExcel.api, params: uploadExcel.params }, file);
            if (!res || !res.data) {
                handleCatchHandle(res.error_message);
                return;
            }
            const {
                data: { status },
            } = res;
            if (status === 0) {
                handleBatchImportOfBinaryProgress();
            }
        }
    };

    const handleBatchImportOfObjectUpload = async (file: File) => {
        const res = await encode(file);
        const payloadData = await upload({ ...res }, userId || '');
        if (payloadData && payloadData.url && payloadData.url !== '') {
            upLoadSuccess(payloadData.url);
        } else {
            onUploadStatusChange(false);
        }
    };

    const onChooseFile = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { uploadExcel } = uploadDataMap;
        const type = uploadExcel?.type || BatchImportType.object;
        const fileList = event.target.files;
        if (!fileList || !fileList.length) {
            return;
        }
        onUploadStatusChange(true);
        const files = Array.from(fileList);
        const file = files[0];

        if (limitFileSize && limitFileSize * 1024 * 1024 <= file.size) {
            sizeMessage && SnackBar.show(sizeMessage);
            return;
        }

        if (type === BatchImportType.object) {
            handleBatchImportOfObjectUpload(file).catch((error) => {
                handleCatchHandle(error);
            });
        } else if (type === BatchImportType.binary) {
            handleBatchImportOfBinaryUpload(file).catch((error) => {
                handleCatchHandle(error);
            });
        }

        event.target.value = '';
    };

    const uploadFile = () => {
        if (inputRef.current) {
            inputRef.current.click();
        }
    };

    return (
        <div className={className + ' umu-batch-import'}>
            <input
                type="file"
                title=""
                ref={inputRef}
                style={{ display: 'none' }}
                accept="application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xlsx"
                onChange={onChooseFile}
            />
            <If condition={isUpLoad}>
                <div className="mask-upload">
                    <UploadProgress rate={uploadProgress} />
                </div>
            </If>

            <If condition={showType === SHOW_TYPE.DESC_PANEL}>
                <UploadIntroduction
                    userId={userId}
                    downFile={downFile}
                    showType={showType}
                    uploadFile={uploadFile}
                    customNotes={customNotes}
                    failedNumber={failedNumber}
                    isHiddenNotes={isHiddenNotes}
                    label={uploadIntroduction.label}
                    downloadDataMap={downloadDataMap}
                >
                    <>
                        <If condition={!isHiddenTableTitle}>
                            <UploadIntroductionTableTitle />
                        </If>
                        <CustomTable
                            miniNumTdWidth={tableTdWidth}
                            isMultilevel={isMultilevel}
                            columns={uploadIntroduction.columns}
                            data={uploadIntroduction.data}
                            className="upload-introduction-table"
                        ></CustomTable>
                    </>
                </UploadIntroduction>
            </If>

            <If condition={showType === SHOW_TYPE.FAIL_PANEL}>
                <UploadFailed
                    callback={callback}
                    downFile={downFile}
                    uploadFile={uploadFile}
                    totalNumber={totalNumber}
                    failedNumber={failedNumber}
                    customFailMsg={customFailMsg}
                >
                    <CustomTable
                        className="upload-failed-table"
                        miniNumTdWidth={tableTdWidth}
                        columns={uploadFailedColumns}
                        isMultilevel={isMultilevel}
                        data={failedCustomTableData.slice(
                            0,
                            limitShowFailedCount === 0 ? failedCustomTableData.length : limitShowFailedCount,
                        )}
                    ></CustomTable>
                </UploadFailed>
            </If>
            <If condition={showType === SHOW_TYPE.SUCCESS_PANEL}>
                <UploadSuccess
                    callback={callback}
                    uploadFile={uploadFile}
                    customSuccessMsg={customSuccessMsg}
                    totalNumber={totalNumber}
                    failedNumber={failedNumber}
                ></UploadSuccess>
            </If>
        </div>
    );
}

export default BatchImport;
